/**
 * The bout bot and the ring's edge, and its own health (the one-bout format, user 2026-10-11; rules: systems/duelRingOut.ts).
 * A last filter on what the bot's mind chose (`BOT_ACT_FILTERS`, systems/duelBot.ts), for the stand-in and the studio's bots:
 * - out of the ring: straight back in (a step toward the middle when time runs short), no moves started out there;
 * - near the edge: never walks or steps out of its own accord (the outward part of a walk or a step is turned along the edge);
 * - the foe out of the ring: holds the line between the foe and the middle, guarding when the foe comes close;
 * - the foe near the edge, the bot nearer the middle: keeps cutting (the blows' shove may put the foe out);
 * - low on health (< 45%) with a heal ready: backs off toward the middle to heal (the mind heals once the foe is 5 m off);
 *   no heal on its bar but another learned art has one: it switches to that art (ordinary play: arts switch in a fight).
 * No randomness here; the World is only read (and `setArt` sent for the switch).
 */
import { DUEL_FLAGS, DUEL_FORMAT } from '../data/duelMatch';
import { MARTIAL_ARTS, SKILLS } from '../data/skills';
import type { Entity, World } from '../world';
import { BOT_ACT_FILTERS, type BotAct } from './duelBot';
import { duelMatchOf } from './duelMatch';
import { duelRingOutOf } from './duelRingOut';
import { skillBarOf } from './skillBar';
import type { WorldSystem } from './types';

/** Numbers of the bot's edge play (metres, seconds, health share). */
export const BOT_RING = {
  /** Inside this far from the edge the bot will not walk outward. */
  edge: 1.0,
  /** Where it holds the line when the foe is out: this far inside the edge. */
  line: 0.9,
  /** Seconds of out-time left at which it steps (dodges) back in rather than walks. */
  stepBack: 5,
  /** Health share under which it backs off to heal. */
  lowHp: 0.45,
} as const;

const unit = (x: number, y: number): [number, number] => {
  const l = Math.hypot(x, y) || 1;
  return [x / l, y / l];
};

/** The heal move on a fighter's bar that is ready (its slot), or -1. */
function healSlot(w: World, e: Entity): number {
  const bar = skillBarOf(w.players.get(e.id)?.progress ?? w.progress);
  return bar.findIndex((id) => {
    const s = id ? SKILLS[id] : undefined;
    return s?.kind === 'heal' && (e.cooldowns[s.id] ?? 0) <= 0 && e.qi >= s.qi;
  });
}

/** The bot's filter (pure but for the art switch it may send). */
export function botRingAct(w: World, botId: number, act: BotAct): BotAct {
  const st = duelMatchOf(w)?.current;
  const b = w.pvpBout;
  if (!DUEL_FLAGS.v2 || !DUEL_FORMAT.ringOut || !st || st.phase !== 'fight' || !b) return act;
  const me = w.get(botId);
  const foe = w.get(b.a === botId ? b.b : b.a);
  if (!me || !foe || me.dead || foe.dead) return act;
  const R = st.ring;
  const [cx, cy] = [me.x - R.x, me.y - R.y];
  const bd = Math.hypot(cx, cy);
  const [ox, oy] = unit(cx, cy); // outward at the bot
  const [inx, iny] = [-ox, -oy];

  // Out: back in at once.
  if (bd > R.r) {
    const left = duelRingOutOf(w)?.infoOf(botId).left ?? DUEL_FORMAT.outLimit;
    return { ...act, move: [inx, iny], skill: null, engage: false, block: false, jump: false, attack: false, dash: left < BOT_RING.stepBack ? [inx, iny] : null };
  }

  let out: BotAct = act;
  // Low on health: a heal ready -> back off toward the middle; none on the bar but another art has one -> switch to it.
  if (me.hp / me.maxHp < BOT_RING.lowHp) {
    if (healSlot(w, me) >= 0) {
      const d = Math.hypot(foe.x - me.x, foe.y - me.y);
      if (d < 5 && act.skill === null) {
        const [ax, ay] = unit(me.x - foe.x, me.y - foe.y);
        const [mx, my] = unit(ax + inx * 0.6, ay + iny * 0.6);
        out = { ...out, move: [mx, my], engage: false, block: d < 2.2 };
      }
    } else {
      const pr = w.players.get(botId)?.progress;
      const other = pr?.arts.find(
        (a) => a !== pr.activeArt && !MARTIAL_ARTS[a]?.hidden && (MARTIAL_ARTS[a]?.skills ?? []).some((id) => SKILLS[id]?.kind === 'heal'),
      );
      if (other && !me.cast) w.apply({ t: 'setArt', id: other }, botId);
    }
  }

  // The foe out: hold the line between it and the middle; guard when it comes close.
  const fd = Math.hypot(foe.x - R.x, foe.y - R.y);
  if (fd > R.r) {
    const [ux, uy] = unit(foe.x - R.x, foe.y - R.y);
    const px = R.x + ux * (R.r - BOT_RING.line);
    const py = R.y + uy * (R.r - BOT_RING.line);
    const gd = Math.hypot(px - me.x, py - me.y);
    const near = Math.hypot(foe.x - me.x, foe.y - me.y) < 2.6;
    out = { ...out, move: gd > 0.4 ? unit(px - me.x, py - me.y) : [0, 0], engage: false, block: near && out.skill === null };
  } else if (fd > R.r - 2 && bd < fd && Math.hypot(foe.x - me.x, foe.y - me.y) < 3) {
    // The foe near the edge, the bot nearer the middle: keep cutting (the shove may put it out).
    out = { ...out, engage: true };
  }

  // Near the edge: never outward of its own accord.
  if (bd > R.r - BOT_RING.edge) {
    const along = (v: readonly [number, number]): [number, number] => {
      const o = v[0] * ox + v[1] * oy;
      return o > 0 ? [v[0] - ox * o, v[1] - oy * o] : [v[0], v[1]];
    };
    out = { ...out, move: along(out.move), dash: out.dash ? along(out.dash) : null };
    if (out.dash && Math.hypot(out.dash[0], out.dash[1]) < 0.2) out = { ...out, dash: null };
  }
  return out;
}

BOT_ACT_FILTERS.push((w, id, a) => botRingAct(w as World, id, a));

/** Registered so the filter's module is part of every World (no state of its own). */
export class DuelBotRingSystem implements WorldSystem {
  readonly name = 'duelBotRing';
  constructor(_h: unknown) {}
}
